// MA-291 — parent activation via claim-code deep link. A brand-new parent
// opens /claim/:code from the teacher's share snippet (MA-290) or a
// send-home note BEFORE they have an account. This module carries the
// per-student claim code across the sign-in/sign-up + onboarding round-trip
// so the parent dash can auto-redeem it (PendingClaimCodeCard) — the
// "conversion moment" of the teacher-activated GTM loop.
//
// Distinct from pendingJoinCode.ts (the CLASS join code, MA-238/MA-292 —
// 6 chars, class-scoped, does no linking today): a CLAIM code is the
// 10-char per-student bearer code migration 0024 mints
// (`generate_enrollment_claim_code`), and redeeming it via
// `claim_class_enrollment` performs the REAL parent↔child linkage. The two
// codes never share a storage key, so a scanned class code can never be
// mistaken for a student claim code (they also differ in length, which
// `isValidClaimCode` pins).
//
// `sessionStorage` (not localStorage), mirroring pendingJoinCode.ts: the
// code is only relevant to THIS tab's current activation attempt. Google's
// OAuth redirect round-trips in the same tab, so the code survives sign-up;
// a magic-link opened in a different context does NOT carry it (the MA-289
// cross-context limitation — the manual entry field on the dash card is the
// honest fallback, never a silent loss).
//
// Storage is injectable (the classRoster.ts `randomFn` convention) so this
// is unit-testable with a fake in-memory Storage — no DOM needed.

// Mirrors migration 0024's generator: 10 chars over the ambiguity-free
// alphabet (no 0/O/1/I).
export const CLAIM_CODE_LENGTH = 10;
export const CLAIM_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export const PENDING_STUDENT_CLAIM_CODE_KEY = "pw_pending_student_claim_code";

/** trim + uppercase — a parent typing a code back should never lose to case
 *  or a copy-paste space (the MA-238 join-code-case-normalize lesson). */
export function normalizeClaimCode(raw: string | null | undefined): string {
  return String(raw ?? "").trim().toUpperCase();
}

/** True iff `code` (already normalized) has exactly the 0024 shape:
 *  10 chars, every one from the ambiguity-free alphabet. A 6-char class
 *  join code NEVER validates here. */
export function isValidClaimCode(code: string): boolean {
  if (code.length !== CLAIM_CODE_LENGTH) return false;
  for (const ch of code) {
    if (!CLAIM_CODE_ALPHABET.includes(ch)) return false;
  }
  return true;
}

function safeStorage(storage: Storage | undefined): Storage | undefined {
  if (storage) return storage;
  return typeof sessionStorage === "undefined" ? undefined : sessionStorage;
}

/** Stash a VALIDATED code for post-auth redemption. Garbage is refused
 *  (never persisted) so the dash card can trust what it reads back. */
export function storePendingClaimCode(code: string, storage?: Storage): void {
  const s = safeStorage(storage);
  if (!s) return;
  const normalized = normalizeClaimCode(code);
  if (!isValidClaimCode(normalized)) return;
  try {
    s.setItem(PENDING_STUDENT_CLAIM_CODE_KEY, normalized);
  } catch {
    /* private mode / quota — the manual entry field remains the fallback */
  }
}

export function readPendingClaimCode(storage?: Storage): string | null {
  const s = safeStorage(storage);
  if (!s) return null;
  try {
    const raw = s.getItem(PENDING_STUDENT_CLAIM_CODE_KEY);
    if (!raw) return null;
    // Defense-in-depth: never hand back a value that no longer validates
    // (e.g. an older build stored something looser).
    return isValidClaimCode(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function clearPendingClaimCode(storage?: Storage): void {
  const s = safeStorage(storage);
  if (!s) return;
  try {
    s.removeItem(PENDING_STUDENT_CLAIM_CODE_KEY);
  } catch {
    /* no-op */
  }
}
